import { Card, Tag } from '@humanforce/mango-ui-react';
import type { PayRunCardDto } from './payRunsApiTypes';
import { buildPayRunCardModel, formatSystemValue } from './payRunsModel';

interface PayRunCardProps {
  payRun: PayRunCardDto;
}

export const statusTone = (value: string): 'pending' | 'positive' | 'critical' | 'caution' | 'neutral' => {
  if (value === 'pending' || value === 'open') return 'pending';
  if (value === 'ready_to_pay' || value === 'returned' || value === 'clear' || value === 'complete') return 'positive';
  if (value === 'admitted' || value === 'calculating') return 'pending';
  if (value === 'failed' || value === 'critical') return 'critical';
  if (value === 'not_sent') return 'neutral';
  return 'caution';
};

export const workflowPresentation = (payRun: PayRunCardDto): {
  label: string;
  tone: 'pending' | 'positive' | 'critical' | 'caution' | 'neutral';
} => {
  const compliance = payRun.statusFacets.find((facet) => facet.type === 'compliance');
  if (payRun.workflowStatus === 'payroll_active') {
    if (compliance?.status === 'clear') return { label: 'Payroll active', tone: 'positive' };
    if (compliance?.status === 'pending') return { label: 'Payroll active', tone: 'pending' };
    return { label: 'Compliance', tone: 'caution' };
  }

  return { label: formatSystemValue(payRun.workflowStatus), tone: statusTone(payRun.workflowStatus) };
};

export const PayRunCard = ({ payRun }: PayRunCardProps) => {
  const model = buildPayRunCardModel(payRun);
  const workflow = workflowPresentation(payRun);
  const calculated = payRun.calculation?.status === 'complete';
  const calculationLabel = payRun.calculation?.status === 'failed' ? 'Calculation failed'
    : payRun.calculation?.status === 'review_required' ? 'Review required'
      : ['admitted', 'calculating'].includes(payRun.calculation?.status ?? '') ? 'Calculating'
        : calculated ? 'Calculated' : payRun.calculation?.status === 'ready' ? 'Ready to calculate' : 'Inputs required';

  return (
    <Card
      href={`/payroll/pay-runs/${encodeURIComponent(payRun.payId)}`}
      header={(
        <div className="mgo-split mgo-align-start mgo-gap-l">
          <div className="mgo-stack mgo-gap-xs">
            <span className="mgo-text-m mgo-weight-strong">{model.title}</span>
            <span className="mgo-text-xs mgo-color-muted">{model.businessRecordName}</span>
          </div>
          <Tag label={workflow.label} tone={workflow.tone} />
        </div>
      )}
    >
      <div className="mgo-stack mgo-gap-l">
        <div className="mgo-stack mgo-gap-xs">
          <span className="mgo-text-xs mgo-color-muted">{model.periodRange} · {model.periodLabel || model.frequencyLabel}</span>
          <div className="mgo-cluster mgo-gap-s">
            <Tag label={model.operatingStateLabel} tone="highlight" />
            <Tag label={calculationLabel} tone={statusTone(payRun.calculation?.status ?? 'not_ready')} />
            <span className="mgo-text-xs mgo-color-muted">{model.payGroupName} · {model.businessRecordName}</span>
          </div>
        </div>

        <div
          className="mgo-grid mgo-grid-cols-2 mgo-gap-0 mgo-background-neutral"
          aria-label="Pay Run statuses"
          style={{ borderBlock: 'var(--mgo-border-neutral)', padding: 'var(--mgo-space-30)' }}
        >
          {model.facets.map((facet) => (
            <div key={`${facet.type}-${facet.value}`} className="mgo-stack mgo-gap-s mgo-align-center">
              <span className="mgo-text-xs mgo-weight-strong">
                {facet.type === 'stp' ? 'STP' : formatSystemValue(facet.type)}
              </span>
              {facet.type === 'compliance' && (facet.blockingCount > 0 || facet.warningCount > 0) ? (
                <div className="mgo-cluster mgo-gap-xs">
                  {facet.blockingCount > 0 && <Tag label={`${facet.blockingCount}`} tone="critical" />}
                  {facet.warningCount > 0 && <Tag label={`${facet.warningCount}`} tone="caution" />}
                </div>
              ) : (
                <Tag label={facet.value === 'not_sent' ? 'Not sent' : facet.label.split(': ')[1]} tone={statusTone(facet.value)} />
              )}
            </div>
          ))}
        </div>

        <div className="mgo-stack mgo-gap-s" aria-label="Pay Run calculations">
          {[
            ['Gross pay', calculated ? model.gross : '—'],
            ['Tax', calculated ? model.tax : '—'],
            ['Super', '—'],
            ['Net Pay', calculated ? model.netPay : '—'],
          ].map(([label, value], index) => (
            <div
              key={label}
              className="mgo-split mgo-gap-l"
              style={index === 3 ? { borderTop: 'var(--mgo-border-neutral)', paddingTop: 'var(--mgo-space-30)' } : undefined}
            >
              <span className={`mgo-text-s ${index === 3 ? 'mgo-weight-strong' : 'mgo-color-muted'}`}>{label}</span>
              <span className="mgo-text-s mgo-weight-strong">{value}</span>
            </div>
          ))}
        </div>

        <div className="mgo-cluster mgo-gap-s">
          <span className="mgo-text-xs mgo-color-muted">Pay date</span>
          <span className="mgo-text-xs mgo-weight-strong">{model.paymentDate}</span>
          <Tag label={model.runTypeLabel} tone="neutral" />
          <Tag label={model.frequencyLabel} tone="neutral" />
          {model.employeeCount != null && <Tag label={`${model.employeeCount} employees`} tone="neutral" />}
        </div>
      </div>
    </Card>
  );
};
